import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as TestIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { schemaMigrationApi, MigrationRule } from '../services/schema-migration-api';

const RULE_TYPES = [
  { value: 'flatToArray', label: 'Flat to Array', description: 'Wrap flat properties into an array' },
  { value: 'arrayToFlat', label: 'Array to Flat', description: 'Extract properties from array to flat' },
  { value: 'renameProperty', label: 'Rename Property', description: 'Rename one or more properties' },
  { value: 'wrapInObject', label: 'Wrap in Object', description: 'Wrap a value in an object' },
  { value: 'unwrapFromObject', label: 'Unwrap from Object', description: 'Extract value from wrapper object' },
  { value: 'addDefault', label: 'Add Default', description: 'Add default value if missing' },
  { value: 'removeProperty', label: 'Remove Property', description: 'Remove deprecated properties' },
  { value: 'coerceType', label: 'Coerce Type', description: 'Convert between types' },
];

export const SchemaMigrationManager: React.FC = () => {
  const [rules, setRules] = useState<MigrationRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<MigrationRule | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Partial<MigrationRule>>({
    id: '',
    type: 'flatToArray',
    description: '',
    matchByRoleName: '',
    priority: 100,
    enabled: true,
    config: {},
  });

  // Test dialog state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testRoleName, setTestRoleName] = useState('');
  const [testRoleVariables, setTestRoleVariables] = useState('{\n  "run_command": "echo hello"\n}');
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<MigrationRule | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    setLoading(true);
    setError(null);

    try {
      const rules = await schemaMigrationApi.getRules();
      setRules(rules);
    } catch (err: any) {
      console.error('Failed to load migration rules:', err);
      setError(`Failed to load rules: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingRule(null);
    setFormData({
      id: '',
      type: 'flatToArray',
      description: '',
      matchByRoleName: '',
      priority: 100,
      enabled: true,
      config: {},
    });
    setDialogOpen(true);
  };

  const openEditDialog = (rule: MigrationRule) => {
    setEditingRule(rule);
    setFormData({
      ...rule,
      matchByRoleName: Array.isArray(rule.matchByRoleName)
        ? rule.matchByRoleName.join(', ')
        : rule.matchByRoleName || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.id || !formData.type) {
      setError('Rule ID and type are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const ruleData: MigrationRule = {
        id: formData.id!,
        type: formData.type!,
        description: formData.description || '',
        matchByRoleName: formData.matchByRoleName
          ? (formData.matchByRoleName as string).split(',').map(s => s.trim()).filter(Boolean)
          : undefined,
        priority: formData.priority,
        enabled: formData.enabled,
        config: buildConfigForType(formData.type!, formData.config),
      };

      if (editingRule) {
        await schemaMigrationApi.updateRule(editingRule.id, ruleData);
        setSuccessMessage(`Rule "${ruleData.id}" updated successfully`);
      } else {
        await schemaMigrationApi.addRule(ruleData);
        setSuccessMessage(`Rule "${ruleData.id}" added successfully`);
      }

      setDialogOpen(false);
      loadRules();
    } catch (err: any) {
      console.error('Failed to save rule:', err);
      setError(`Failed to save rule: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const buildConfigForType = (type: string, existingConfig: any): any => {
    // Return existing config - the form fields update it directly
    return existingConfig || {};
  };

  const handleDelete = async () => {
    if (!ruleToDelete) return;

    setDeleting(true);
    setError(null);

    try {
      await schemaMigrationApi.deleteRule(ruleToDelete.id);
      setSuccessMessage(`Rule "${ruleToDelete.id}" deleted successfully`);
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
      loadRules();
    } catch (err: any) {
      console.error('Failed to delete rule:', err);
      setError(`Failed to delete rule: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleEnabled = async (rule: MigrationRule) => {
    try {
      await schemaMigrationApi.updateRule(rule.id, {
        ...rule,
        enabled: !rule.enabled,
      });
      loadRules();
    } catch (err: any) {
      console.error('Failed to toggle rule:', err);
      setError(`Failed to toggle rule: ${err.message}`);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const roleVariables = JSON.parse(testRoleVariables);
      const result = await schemaMigrationApi.testMigration(testRoleName, roleVariables);
      setTestResult(result);
    } catch (err: any) {
      console.error('Test failed:', err);
      setTestResult({
        error: err.message || 'Test failed',
        wasTransformed: false,
        appliedRules: [],
      });
    } finally {
      setTesting(false);
    }
  };

  const renderConfigFields = () => {
    switch (formData.type) {
      case 'flatToArray':
        return (
          <>
            <TextField
              fullWidth
              label="Target Array Property"
              value={formData.config?.targetArrayProperty || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, targetArrayProperty: e.target.value }
              })}
              helperText="The array property name in the new schema (e.g., 'commands')"
              margin="normal"
            />
            <TextField
              fullWidth
              label="Source Properties"
              value={formData.config?.sourceProperties?.join(', ') || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: {
                  ...formData.config,
                  sourceProperties: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                }
              })}
              helperText="Comma-separated properties to move into array (e.g., 'run_command, change_directory, exec')"
              margin="normal"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.config?.preserveExtraProperties !== false}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, preserveExtraProperties: e.target.checked }
                  })}
                />
              }
              label="Preserve extra properties at top level"
            />
          </>
        );

      case 'arrayToFlat':
        return (
          <>
            <TextField
              fullWidth
              label="Source Array Property"
              value={formData.config?.sourceArrayProperty || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, sourceArrayProperty: e.target.value }
              })}
              helperText="The array property to flatten (e.g., 'commands')"
              margin="normal"
            />
            <TextField
              fullWidth
              type="number"
              label="Extract from Index"
              value={formData.config?.extractFromIndex ?? 0}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, extractFromIndex: parseInt(e.target.value) || 0 }
              })}
              helperText="Which array index to extract (default: 0)"
              margin="normal"
            />
          </>
        );

      case 'renameProperty':
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Renames (JSON)"
            value={JSON.stringify(formData.config?.renames || [], null, 2)}
            onChange={(e) => {
              try {
                const renames = JSON.parse(e.target.value);
                setFormData({
                  ...formData,
                  config: { ...formData.config, renames }
                });
              } catch {
                // Invalid JSON, keep as-is
              }
            }}
            helperText='Array of {from, to} objects: [{"from": "old", "to": "new"}]'
            margin="normal"
          />
        );

      case 'wrapInObject':
        return (
          <>
            <TextField
              fullWidth
              label="Property to Wrap"
              value={formData.config?.propertyToWrap || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, propertyToWrap: e.target.value }
              })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Wrapper Property Name"
              value={formData.config?.wrapperPropertyName || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, wrapperPropertyName: e.target.value }
              })}
              margin="normal"
            />
          </>
        );

      case 'unwrapFromObject':
        return (
          <>
            <TextField
              fullWidth
              label="Property to Unwrap"
              value={formData.config?.propertyToUnwrap || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, propertyToUnwrap: e.target.value }
              })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Inner Property Name"
              value={formData.config?.innerPropertyName || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, innerPropertyName: e.target.value }
              })}
              margin="normal"
            />
          </>
        );

      case 'addDefault':
        return (
          <>
            <TextField
              fullWidth
              label="Property"
              value={formData.config?.property || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, property: e.target.value }
              })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Default Value (JSON)"
              value={JSON.stringify(formData.config?.defaultValue ?? '')}
              onChange={(e) => {
                try {
                  const defaultValue = JSON.parse(e.target.value);
                  setFormData({
                    ...formData,
                    config: { ...formData.config, defaultValue }
                  });
                } catch {
                  // Keep as string
                  setFormData({
                    ...formData,
                    config: { ...formData.config, defaultValue: e.target.value }
                  });
                }
              }}
              margin="normal"
            />
          </>
        );

      case 'removeProperty':
        return (
          <TextField
            fullWidth
            label="Properties to Remove"
            value={formData.config?.properties?.join(', ') || ''}
            onChange={(e) => setFormData({
              ...formData,
              config: {
                ...formData.config,
                properties: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
              }
            })}
            helperText="Comma-separated list of properties to remove"
            margin="normal"
          />
        );

      case 'coerceType':
        return (
          <>
            <TextField
              fullWidth
              label="Property"
              value={formData.config?.property || ''}
              onChange={(e) => setFormData({
                ...formData,
                config: { ...formData.config, property: e.target.value }
              })}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>From Type</InputLabel>
              <Select
                value={formData.config?.fromType || 'string'}
                label="From Type"
                onChange={(e) => setFormData({
                  ...formData,
                  config: { ...formData.config, fromType: e.target.value }
                })}
              >
                <MenuItem value="string">String</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
                <MenuItem value="array">Array</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>To Type</InputLabel>
              <Select
                value={formData.config?.toType || 'array'}
                label="To Type"
                onChange={(e) => setFormData({
                  ...formData,
                  config: { ...formData.config, toType: e.target.value }
                })}
              >
                <MenuItem value="string">String</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
                <MenuItem value="array">Array</MenuItem>
              </Select>
            </FormControl>
          </>
        );

      default:
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Config (JSON)"
            value={JSON.stringify(formData.config || {}, null, 2)}
            onChange={(e) => {
              try {
                setFormData({ ...formData, config: JSON.parse(e.target.value) });
              } catch {
                // Invalid JSON
              }
            }}
            margin="normal"
          />
        );
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Schema Migration Rules</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<TestIcon />}
                onClick={() => setTestDialogOpen(true)}
              >
                Test Migration
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadRules}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAddDialog}
              >
                Add Rule
              </Button>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure rules to automatically transform Ansible role variables when schemas change between environments.
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : rules.length === 0 ? (
            <Alert severity="info">
              No migration rules configured. Click "Add Rule" to create one.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Enabled</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Match By</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <Switch
                          checked={rule.enabled !== false}
                          onChange={() => handleToggleEnabled(rule)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {rule.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rule.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={RULE_TYPES.find(t => t.value === rule.type)?.label || rule.type}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {rule.matchByRoleName && (
                          <Typography variant="body2">
                            Role: {Array.isArray(rule.matchByRoleName)
                              ? rule.matchByRoleName.join(', ')
                              : rule.matchByRoleName}
                          </Typography>
                        )}
                        {rule.matchByRoleUuid && (
                          <Typography variant="body2">
                            UUID: {Array.isArray(rule.matchByRoleUuid)
                              ? `${rule.matchByRoleUuid.length} UUIDs`
                              : rule.matchByRoleUuid}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{rule.priority || 0}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(rule)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setRuleToDelete(rule);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Rule Type Reference */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Rule Type Reference</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Example</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><Chip label="flatToArray" size="small" /></TableCell>
                    <TableCell>Wrap flat properties into an array</TableCell>
                    <TableCell><code>{`{run_command: "x"} → {commands: [{run_command: "x"}]}`}</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Chip label="arrayToFlat" size="small" /></TableCell>
                    <TableCell>Extract from array to flat structure</TableCell>
                    <TableCell><code>{`{commands: [{run_command: "x"}]} → {run_command: "x"}`}</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Chip label="renameProperty" size="small" /></TableCell>
                    <TableCell>Rename properties</TableCell>
                    <TableCell><code>{`{oldName: "x"} → {newName: "x"}`}</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Chip label="addDefault" size="small" /></TableCell>
                    <TableCell>Add default value if missing</TableCell>
                    <TableCell><code>{`{} → {prop: "default"}`}</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Chip label="removeProperty" size="small" /></TableCell>
                    <TableCell>Remove deprecated properties</TableCell>
                    <TableCell><code>{`{a: 1, deprecated: 2} → {a: 1}`}</code></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRule ? `Edit Rule: ${editingRule.id}` : 'Add Migration Rule'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Rule ID"
            value={formData.id}
            onChange={(e) => setFormData({ ...formData, id: e.target.value })}
            disabled={!!editingRule}
            helperText="Unique identifier for this rule (e.g., 'windows-run-command-v2')"
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Rule Type</InputLabel>
            <Select
              value={formData.type}
              label="Rule Type"
              onChange={(e) => setFormData({ ...formData, type: e.target.value, config: {} })}
            >
              {RULE_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label} - {type.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Matching Criteria</Typography>

          <TextField
            fullWidth
            label="Match by Role Name"
            value={formData.matchByRoleName}
            onChange={(e) => setFormData({ ...formData, matchByRoleName: e.target.value })}
            helperText="Role name(s) to match. Supports wildcards (*). Comma-separated for multiple."
            margin="normal"
          />

          <TextField
            fullWidth
            type="number"
            label="Priority"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
            helperText="Higher priority rules run first"
            margin="normal"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled !== false}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            }
            label="Rule Enabled"
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Rule Configuration</Typography>

          {renderConfigFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (editingRule ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Rule</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete rule <strong>"{ruleToDelete?.id}"</strong>?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Test Schema Migration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test how migration rules will transform role variables.
          </Typography>

          <TextField
            fullWidth
            label="Role Name"
            value={testRoleName}
            onChange={(e) => setTestRoleName(e.target.value)}
            helperText="Enter the Ansible role name to match against rules"
            margin="normal"
          />

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Role Variables (JSON)"
            value={testRoleVariables}
            onChange={(e) => setTestRoleVariables(e.target.value)}
            margin="normal"
            sx={{ fontFamily: 'monospace' }}
          />

          <Button
            variant="contained"
            startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
            onClick={handleTest}
            disabled={testing || !testRoleName}
            sx={{ mt: 2 }}
          >
            Run Test
          </Button>

          {testResult && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Result</Typography>

              {testResult.error ? (
                <Alert severity="error">{testResult.error}</Alert>
              ) : (
                <>
                  <Alert severity={testResult.wasTransformed ? 'success' : 'info'} sx={{ mb: 2 }}>
                    {testResult.wasTransformed
                      ? `Transformed using ${testResult.appliedRules.length} rule(s): ${testResult.appliedRules.join(', ')}`
                      : 'No transformation applied (no matching rules or already in correct format)'}
                  </Alert>

                  {testResult.errors?.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Errors: {testResult.errors.join(', ')}
                    </Alert>
                  )}

                  <Typography variant="subtitle2" gutterBottom>Transformed Output:</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <pre style={{ margin: 0, overflow: 'auto', fontSize: '0.85rem' }}>
                      {JSON.stringify(testResult.transformed, null, 2)}
                    </pre>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
